import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { CartLineModel, OrderLineModel, ProductInventoryDto, ProductModel, WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import { getPageSource } from "../trackingHelper";

const productClickTrackingBuilder = (
    carouselTitle: string,
    category: Category | undefined,
    carouselType: string,
    currentPageUrl: string,
    product: CartLineModel | ProductContextModel | OrderLineModel,
    state: ApplicationState,
    inventory?: ProductInventoryDto,
    parentProductId?: string | undefined,
    warehouses?: WarehouseDto[] | undefined,
    itemLinkClick?: string | undefined,
) => {
    let listName = "";
    let condition = "";
    let vendorStockStatus = "";
    let vendorStockQty = "";
    const url = currentPageUrl.toLowerCase();
    const isOrderDetail = url.includes("orders/details");
    const isQuoteDetail = url.includes("quote/quotedetail");

    /* listName */
    if (carouselTitle === "" && category?.shortDescription !== undefined) {
        listName = "Category Results";
    } else if (carouselTitle !== undefined && category?.shortDescription !== undefined) {
        listName = carouselTitle;
    } else if (carouselType !== undefined && category?.shortDescription === undefined) {
        listName = carouselTitle;
    } else if (carouselTitle === undefined && category?.shortDescription !== undefined) {
        listName = "Category Results";
    }

    listName = getPageSource(currentPageUrl, listName);
    if (parentProductId) {
        listName = "Other Buying Options";
    }

    /* description */
    const description = "product" in product
        ? product.product.productTitle
        : product.shortDescription || ("description" in product ? product.description : "");

    /* sku */
    let sku = "product" in product ? product.product.productNumber : "productErpNumber" in product ? product.productErpNumber : "";
    if (sku === undefined || sku === "") {
        sku = "productName" in product ? String(product.productName) : "erpNumber" in product ? String(product.erpNumber) : "";
    }

    /* condition */
    if ("product" in product) {
        condition = product.product?.properties?.condition ?? "";
    } else if ("line" in product) {
        condition = product.properties?.cartLineCondition ?? product.properties?.condition ?? "";
    } else if ("lineNumber" in product) {
        condition = product.properties?.condition ?? "";
    }

    /* stock */
    const productId = "product" in product ? product.product.id : "productId" in product ? product.productId : "";
    let productInfo = "product" in product ? product.productInfo : undefined;
    if (!productInfo) {
        const productData = getProductState(state, productId as string).value as ProductModel;
        if (productData) {
            productInfo =
                state.pages.productDetails.productInfosById?.[productId as string] ??
                createFromProduct(productData);
        }
    }

    if (inventory) {
        if (!productInfo) {
            productInfo = {
                productId: "productId" in product ? product.productId ?? '' : '',
                inventory,
                qtyOrdered: "qtyOrdered" in product ? product.qtyOrdered ?? 0 : 0,
                productDetailPath: '',
                unitOfMeasure: "unitOfMeasure" in product ? product.unitOfMeasure ?? '' : ''
            }
        } else {
            productInfo = { ...productInfo, inventory };
        }
    }

    const stock = currentPageUrl.includes("Orders/Details")
        ?
        productInfo?.inventory?.qtyOnHand !== undefined &&
            productInfo?.inventory?.qtyOnHand <= 0 &&
            "properties" in product && product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true"
            ? "DirectShip"
            : (
                productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                    o =>
                        o.unitOfMeasure.toLowerCase() ===
                        (productInfo?.unitOfMeasure?.toLowerCase() || ""),
                )?.availability || undefined
            )?.message ?? ""
        :
        "product" in product
            ? // Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
            productInfo?.inventory?.qtyOnHand !== undefined &&
                productInfo?.inventory?.qtyOnHand <= 0 &&
                product.product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true"
                ? "DirectShip"
                : (
                    productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                        o =>
                            o.unitOfMeasure.toLowerCase() ===
                            (productInfo?.unitOfMeasure?.toLowerCase() || ""),
                    )?.availability || undefined
                )?.message
            : "";

    /* stock quantity */
    const stkQty = isOrderDetail || isQuoteDetail
        ?
        productInfo?.inventory?.qtyOnHand ?? 0
        :
        "product" in product
            ? productInfo?.inventory?.qtyOnHand
            : "qtyOnHand" in product
                ?
                product.qtyOnHand
                : "0";

    /* Vendor Stock Status*/
    if (
        (productInfo?.inventory?.inventoryWarehousesDtos &&
            productInfo?.inventory?.inventoryWarehousesDtos.length > 0) || (warehouses)
    ) {
        const warehouseDtos = productInfo?.inventory?.inventoryWarehousesDtos ? productInfo?.inventory?.inventoryWarehousesDtos[0].warehouseDtos : warehouses;
        if (warehouseDtos) {
            for (const warehouseData of warehouseDtos) {
                if (warehouseData.name.toLowerCase() === "vendor stock" || warehouseData.name.toLowerCase() === "blind ship") {
                    vendorStockQty = warehouseData.qty.toString();
                }
                if (warehouseData.qty > 0 && warehouseData.name.toLowerCase() === "blind ship") {
                    vendorStockStatus = "blindship";
                    break;
                }
                if (
                    "product" in product ? product.product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true" : "isDirectShipFromManufacturer" in product.properties ? product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true" : "false"
                ) {
                    if (
                        (warehouseData.qty > 0 && warehouseData.name.toLowerCase() === "vendor stock") ||
                        (warehouseData.qty === 0 && warehouseData.name.toLowerCase() === "blind ship")
                    ) {
                        vendorStockStatus = "crossdock";
                        break;
                    }
                }
            }
        }
    }
    if ("product" in product ? product.product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true" : "isDirectShipFromManufacturer" in product.properties ? product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true" : "false") {
        vendorStockStatus = "directship";
    }

    /* position */
    const index = "product" in product ? product.idx : 0;
    const position = "product" in product ? product.idx! + 1 : "line" in product ? product.line : 0;

    /* dataLayer object */
    const dataLayerProductClick = {
        event: "productClick",
        ecommerce: {
            click: {
                actionField: { list: listName, action: "click" }, // Optional list property.
                products: [
                    {
                        name: description,
                        id: sku,
                        price:
                            "pricing" in product
                                ? product.pricing?.actualPrice
                                : "product" in product
                                    ? product.productInfo.pricing?.actualPrice
                                    : product.unitNetPrice,
                        brand: "product" in product ? product.product.brand?.name : product.brand?.name,
                        condition,
                        category: category?.shortDescription ? category?.shortDescription : "properties" in product ? product.properties?.categoryName ?? "" : "",
                        stock,
                        stockqty: stkQty,
                        position: String(index) !== "undefined" ? Number(index) + 1 : position,
                        vendorstockstatus: vendorStockStatus,
                        vendorstockqty: vendorStockQty,
                        item_link_click: itemLinkClick,
                    },
                ],
            },
        },
    };
    return dataLayerProductClick;
};

export default productClickTrackingBuilder;
